import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { apiClient } from './client';
import { ConnectionRequestPayload } from './types';
import { getErrorMessage, logError } from '../lib/errorHandling';

/**
 * Ответ от API при успешной отправке заявки
 */
export interface ConnectionRequestResponse {
  /** ID созданной заявки */
  id: string;
  /** Сообщение об успехе */
  message: string;
}

/**
 * Отправка заявки на подключение
 * @param data - Данные заявки
 * @returns Promise с результатом создания заявки
 * 
 * @example
 * ```ts
 * const response = await sendConnectionRequest({
 *   name: 'Иван Иванов',
 *   phone: '+79001234567',
 *   address: 'г. Москва, ул. Ленина, д. 1',
 *   services: [ConnectionRequestService.INTERNET],
 * });
 * ```
 */
export const sendConnectionRequest = async (
  data: ConnectionRequestPayload
): Promise<ConnectionRequestResponse> => {
  try {
    const response = await apiClient.post<ConnectionRequestResponse>(
      '/public/connection-requests',
      data
    );
    return response.data;
  } catch (error) {
    logError(error, 'sendConnectionRequest');
    throw error;
  }
};

/**
 * Опции для колбэков mutation
 */
export interface ConnectionRequestMutationOptions {
  /** Колбэк при успешной отправке */
  onSuccess?: (data: ConnectionRequestResponse) => void;
  /** Колбэк при ошибке */
  onError?: (error: string) => void;
}

/**
 * Hook для отправки заявки на подключение с обработкой успеха и ошибок
 * 
 * @param options - Опции для обработки успеха и ошибок
 * @returns Mutation hook с методами для отправки заявки
 * 
 * @example
 * ```ts
 * const { mutate, isLoading, isError, error } = useSendConnectionRequest({
 *   onSuccess: (data) => {
 *     console.log('Заявка отправлена:', data.id);
 *     showSuccessMessage('Заявка успешно отправлена!');
 *   },
 *   onError: (errorMessage) => {
 *     console.error('Ошибка:', errorMessage);
 *     showErrorMessage(errorMessage);
 *   },
 * });
 * 
 * // Отправка заявки
 * mutate({
 *   name: 'Иван Иванов',
 *   phone: '+79001234567',
 *   address: 'г. Москва, ул. Ленина, д. 1',
 *   services: [ConnectionRequestService.INTERNET],
 * });
 * ```
 */
export const useSendConnectionRequest = (
  options?: ConnectionRequestMutationOptions
): UseMutationResult<ConnectionRequestResponse, unknown, ConnectionRequestPayload> => {
  return useMutation({
    mutationFn: sendConnectionRequest,
    onSuccess: (data) => {
      if (options?.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      logError(error, 'useSendConnectionRequest');
      
      if (options?.onError) {
        options.onError(errorMessage);
      }
    },
  });
};
